<script setup lang="ts">
import { Heading, Text } from '~ds/components/ui/typography';
import KUIUnifiedConfirmPrize from '~kui/components/KUI/Unified/ConfirmPrize/KUIUnifiedConfirmPrize.vue';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

import type { PrizeOutcome } from './types';

import { PRIZE_OUTCOME } from './types';

interface Props {
  title: string;
  termsTo: string;
  imageUrl?: string;
  outcome?: PrizeOutcome;
  confirmLoading?: boolean;
  confirmError?: string | null;
  /** Game-of-skill question the entrant must answer before confirming. */
  skillQuestion?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  imageUrl: '/images/kui/prize.webp',
  outcome: null,
  confirmLoading: false,
  confirmError: null,
  skillQuestion: null,
});

const emit = defineEmits<{
  confirmEntry: [];
  cancelPrizeDraw: [];
}>();

const skillAnswer = defineModel<string>('skillAnswer', { default: '' });

const showAnswerPlayback = computed(() => !!props.skillQuestion && !!skillAnswer.value.trim());

const { t } = useI18n();
const { SLIDE_UP, TRANSITION } = useMotionPresets();

const showConfirm = ref(false);

const openConfirmPrize = () => {
  showConfirm.value = true;
};

const handleOpenChange = (isOpen: boolean) => {
  if (!isOpen && showConfirm.value) {
    emit('cancelPrizeDraw');
  }
};

const handleCancel = () => {
  showConfirm.value = false;
  emit('cancelPrizeDraw');
};

watch(() => props.confirmLoading, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading && !props.confirmError) {
    showConfirm.value = false;
  }
});

defineExpose({ openConfirmPrize });
</script>

<template>
  <div class="flex w-full h-full flex-col items-center justify-center gap-4">
    <!-- Outcome display -->
    <template v-if="outcome">
      <!-- Confirmed -->
      <template v-if="outcome === PRIZE_OUTCOME.Confirmed">
        <Heading
          size="md"
          as="h2"
          class="max-w-xs text-center leading-tight sm:text-2xl"
        >
          {{ t('unified.activity.completion.prize.confirmedHeading') }}
        </Heading>
        <Text class="text-center max-w-xs">
          {{ t('unified.activity.completion.prize.confirmedBody', { prizeName: title }) }}
        </Text>
        <Text
          v-if="showAnswerPlayback"
          class="max-w-xs rounded-2xl bg-background p-4 text-left"
        >
          <span class="block text-sm font-semibold">
            {{ t('unified.activity.completion.prize.skillAnswerLabel') }}
          </span>
          <span class="line-clamp-4">{{ skillAnswer }}</span>
        </Text>
        <Text class="text-center max-w-xs opacity-70">
          {{ skillQuestion ? t('unified.activity.completion.prize.confirmedSubtextJudged') : t('unified.activity.completion.prize.confirmedSubtext') }}
        </Text>
        <NuxtImg
          :src="imageUrl"
          alt=""
          aria-hidden="true"
          class="size-64 sm:size-80 md:size-96"
        />
      </template>

      <!-- Phone Skipped -->
      <template v-else-if="outcome === PRIZE_OUTCOME.PhoneSkipped">
        <Heading
          size="md"
          as="h2"
          class="max-w-xs text-center leading-tight sm:text-2xl"
        >
          {{ t('unified.activity.completion.prize.phoneSkippedHeading') }}
        </Heading>
        <Text class="text-center max-w-xs">
          {{ t('unified.activity.completion.prize.phoneSkippedBody') }}
        </Text>
        <Text class="text-center max-w-xs opacity-70">
          {{ t('unified.activity.completion.prize.phoneSkippedSubtext') }}
        </Text>
        <NuxtImg
          :src="imageUrl"
          alt=""
          aria-hidden="true"
          class="size-64 sm:size-80 md:size-96"
        />
      </template>

      <!-- Cancelled -->
      <template v-else-if="outcome === PRIZE_OUTCOME.Cancelled">
        <Heading
          size="md"
          as="h2"
          class="max-w-xs text-center leading-tight sm:text-2xl"
        >
          {{ t('unified.activity.completion.prize.cancelledHeading') }}
        </Heading>
        <Text class="text-center max-w-xs">
          {{ t('unified.activity.completion.prize.cancelledBody') }}
        </Text>
        <NuxtImg
          src="/images/kui/unified/thumbs-up.webp"
          alt=""
          aria-hidden="true"
          class="size-64 sm:size-80 md:size-96"
        />
      </template>
    </template>

    <!-- Entry display (no outcome yet) -->
    <template v-else>
      <motion.div
        class="text-center"
        v-bind="SLIDE_UP"
        :transition="TRANSITION"
      >
        <Heading
          size="md"
          as="h2"
          class="max-w-xs leading-tight sm:text-2xl"
        >
          {{ title }}
        </Heading>
      </motion.div>

      <motion.div
        v-bind="SLIDE_UP"
        :transition="{ ...TRANSITION, delay: 0.1 }"
      >
        <NuxtImg
          :src="imageUrl"
          :alt="title"
          class="size-64 sm:size-80 md:size-96"
        />
      </motion.div>

      <KUIUnifiedConfirmPrize
        v-model:skill-answer="skillAnswer"
        :open="showConfirm"
        :prize-name="title"
        :terms-to="termsTo"
        :loading="confirmLoading"
        :error="confirmError"
        :skill-question="skillQuestion"
        @update:open="handleOpenChange"
        @confirm-entry="emit('confirmEntry')"
        @cancel="handleCancel"
      />
    </template>
  </div>
</template>
